import { Button } from '@tunasong/ui-lib'
import { useEffect, useId, useRef } from 'react'
import type { FC } from 'react'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import type { MicAccessFailure } from './mic-access.js'
import { useVocalCopy } from './use-vocal-copy.js'
import styles from './pitch-mic-blocked-panel.module.css'

/**
 * The capture window could not open the microphone (GH #2327).
 *
 * Before this existed, the rejection went nowhere: calibration sat on "Listening…" and an item sat
 * on its reference label, with every button disabled. The instructions are UI labels, not verdicts
 * on the singer, so they are written here rather than in the lexicon. Copy by Joni (#2339 review).
 */

const COPY: Record<MicAccessFailure, { title: string; body: string; still: string }> = {
  /**
   * NotAllowedError is also what the browser reports when the operating system denies it, and then
   * the site already shows as allowed; the last clause is for that user. "Beside the address", not
   * "click the icon": touch has no click, and iOS Safari has no icon there.
   */
  blocked: {
    title: 'Microphone blocked',
    body: "Tunasong needs your microphone to hear you sing. Allow it for this site in your browser — usually from the icon beside the address — then try again. If it's already allowed there, check your computer's privacy settings for the microphone.",
    still: 'Still blocked — change the setting first, then try again.',
  },
  missing: {
    title: 'No microphone found',
    body: 'Plug in a microphone or headset, or choose one in your system sound settings, then try again.',
    still: 'Still no microphone found.',
  },
  /** Usually another app, but a driver or hardware fault reads the same: "may be", not "is" */
  'in-use': {
    title: 'Microphone in use',
    body: 'Another app may be using your microphone, such as a call or a recording app. Close it, then try again.',
    still: 'Still in use.',
  },
}

export interface PitchMicBlockedPanelProps {
  failure: MicAccessFailure
  /**
   * The last Try again failed the same way. Chrome does not prompt again after Block, so without
   * a line saying so the retry looks like a dead button.
   */
  repeated: boolean
  /** "Cancel" in calibration; "End session" from an exercise, where leaving drops the session's results. */
  cancelLabel: string
  /** Run the same take again: calibration stays on its step, an item stays on its question. */
  onRetry: () => void
  onCancel: () => void
}

export const PitchMicBlockedPanel: FC<PitchMicBlockedPanelProps> = props => {
  const { failure, repeated, cancelLabel, onRetry, onCancel } = props
  const privacy = useVocalCopy('vocal-privacy')
  const { title, body, still } = COPY[failure]

  /**
   * The button that started the take unmounted with it, so focus fell to the page. Land it on the
   * heading, as the feedback panel does (#2330): a screen reader reads the state from there, which
   * is also why the body is not an alert, which would read it twice.
   */
  const headingRef = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  /**
   * Try again unmounts this panel, and a repeat remounts it with the "Still…" line already in the
   * live region, which screen readers do not announce. The focused heading describes itself by the
   * line, so it is read on arrival (Joni, #2345).
   */
  const statusId = useId()

  return (
    <section className={styles.panel} aria-label={title}>
      <h2 ref={headingRef} tabIndex={-1} className={styles.title} aria-describedby={repeated ? statusId : undefined}>
        {title}
      </h2>
      <p className={styles.problem}>{body}</p>
      {/* Permission is only the question when access was refused; for the other two it is settled */}
      {failure === 'blocked' && privacy ? (
        <p className={styles.privacy}>
          <TheoryGlyphs>{privacy.body}</TheoryGlyphs>
        </p>
      ) : null}
      {/* Mounted empty on the first failure, so a polite live region announces the repeat */}
      <p id={statusId} className={styles.still} role="status">
        {repeated ? still : ''}
      </p>
      <div className={styles.actions}>
        <Button variant="contained" color="primary" onClick={onRetry}>
          Try again
        </Button>
        <Button variant="text" onClick={onCancel}>
          {cancelLabel}
        </Button>
      </div>
    </section>
  )
}
